import React, { useState, useEffect, useCallback } from 'react';
import { User, Save, X, Edit3, Camera } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useEmailAuth } from '../../hooks/useEmailAuth';
import { supabase } from '../../config/supabase';

const UserProfile = ({ isVisible, onClose }) => {
  const { user, isAuthenticated } = useAuth();
  const { getCurrentUser, triggerProfileRefresh } = useEmailAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    profilePicture: null
  });
  const [tempProfileData, setTempProfileData] = useState({
    name: '',
    profilePicture: null
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasPendingSync, setHasPendingSync] = useState(false);

  // Test auth endpoint connectivity
  // eslint-disable-next-line no-unused-vars -- dead code, kept for now
  const testAuthEndpoint = async () => {
    try {
      console.log('🔍 Testing Supabase Auth endpoint connectivity...');
      const { error } = await supabase.auth.getSession();
      if (error) {
        console.error('❌ Auth endpoint test failed:', error);
        return false;
      }
      console.log('✅ Auth endpoint is accessible');
      return true;
    } catch (error) {
      console.error('❌ Auth endpoint connectivity test failed:', error);
      return false;
    }
  };

  // Comprehensive Supabase diagnostics
  const runSupabaseDiagnostics = async () => {
    console.log('🔍 Running comprehensive Supabase diagnostics...');
    
    // 1. Test DNS resolution and different endpoints
    try {
      console.log('🌐 Testing DNS resolution...');
      await fetch('https://bjhtlokaafsnoscgecct.supabase.co/', { 
        method: 'GET',
        mode: 'no-cors' // Bypass CORS for basic connectivity test
      });
      console.log('✅ DNS resolution working - can reach Supabase domain');
    } catch (dnsError) {
      console.error('❌ DNS resolution failed:', dnsError);
    }
    
    // Test different Supabase endpoints to see which are blocked
    const endpoints = [
      '/rest/v1/rpc/version',
      '/auth/v1/settings', 
      '/storage/v1/buckets'
    ];
    
    // CONFIGURATION DEBUG: Test project status and settings
    console.log('🔍 PROJECT CONFIGURATION DEBUG:');
    try {
      // Test if project is paused/suspended
      const projectHealthResponse = await fetch(`https://bjhtlokaafsnoscgecct.supabase.co/rest/v1/`, {
        method: 'GET',
        headers: {
          'apikey': localStorage.getItem('supabase_anon_key') || 'test'
        }
      });
      console.log('Project health check status:', projectHealthResponse.status);
      
      if (projectHealthResponse.status === 401) {
        console.error('❌ PROJECT AUTHENTICATION FAILED - Invalid API key or project suspended');
      } else if (projectHealthResponse.status === 404) {
        console.log('✅ Project accessible (404 is normal for root endpoint)');
      }
    } catch (projectError) {
      console.error('❌ Project health check failed:', projectError.message);
    }
    
    for (const endpoint of endpoints) {
      try {
        console.log(`🔍 Testing endpoint: ${endpoint}`);
        const testResponse = await fetch(`https://bjhtlokaafsnoscgecct.supabase.co${endpoint}`, {
          method: 'GET',
          mode: 'cors',
          headers: {
            'apikey': localStorage.getItem('supabase_anon_key') || 'test'
          }
        });
        console.log(`✅ Endpoint ${endpoint} accessible (status: ${testResponse.status})`);
      } catch (endpointError) {
        console.error(`❌ Endpoint ${endpoint} failed:`, endpointError.message);
      }
    }
    
    // 2. Test Supabase configuration
    console.log('🔧 Testing Supabase configuration...');
    console.log('Supabase URL:', supabase.supabaseUrl);
    console.log('Supabase Key (first 20 chars):', supabase.supabaseKey?.substring(0, 20) + '...');
    
    // Check if supabase client was created successfully
    if (!supabase) {
      console.error('❌ Supabase client is null - configuration failed');
      return false;
    }
    
    // Test environment variables vs localStorage
    console.log('Environment URL:', process.env.REACT_APP_SUPABASE_URL ? 'Set' : 'Not set');
    console.log('Environment Key:', process.env.REACT_APP_SUPABASE_ANON_KEY ? 'Set' : 'Not set');
    console.log('LocalStorage URL:', localStorage.getItem('supabase_url') ? 'Set' : 'Not set');
    console.log('LocalStorage Key:', localStorage.getItem('supabase_anon_key') ? 'Set' : 'Not set');
    
    // Show actual values for debugging app-level auth conflict
    console.log('🔍 DEBUGGING APP-LEVEL AUTH:');
    console.log('Actual localStorage URL:', localStorage.getItem('supabase_url'));
    console.log('Actual localStorage Key (first 50 chars):', localStorage.getItem('supabase_anon_key')?.substring(0, 50) + '...');
    console.log('Expected URL:', 'https://bjhtlokaafsnoscgecct.supabase.co');
    
    // CONFIGURATION DEBUG: Decode and analyze the JWT token
    const storedKey = localStorage.getItem('supabase_anon_key');
    if (storedKey) {
      try {
        const keyParts = storedKey.split('.');
        if (keyParts.length === 3) {
          // Decode the payload (second part)
          const payload = JSON.parse(atob(keyParts[1]));
          console.log('🔍 JWT PAYLOAD ANALYSIS:');
          console.log('Issuer (iss):', payload.iss);
          console.log('Role:', payload.role);
          console.log('Issued at:', new Date(payload.iat * 1000));
          console.log('Expires at:', new Date(payload.exp * 1000));
          console.log('Project reference:', payload.ref);
          
          // Check if token is expired
          const now = Date.now() / 1000;
          if (payload.exp && payload.exp < now) {
            console.error('❌ ANON KEY IS EXPIRED!');
          } else {
            console.log('✅ Anon key is not expired');
          }
          
          // Check if issuer matches URL
          if (payload.iss && !payload.iss.includes('bjhtlokaafsnoscgecct')) {
            console.error('❌ JWT ISSUER MISMATCH - token is for different project!');
            console.error('Token issuer:', payload.iss);
            console.error('Expected project:', 'bjhtlokaafsnoscgecct');
            console.error('🔍 This means the user signed up on a different Supabase project');
            
            // Offer to sign out the user so they can sign up on the correct project
            // eslint-disable-next-line no-restricted-globals
            const shouldSignOut = confirm(
              '❌ Authentication Mismatch!\n\n' +
              'Your account was created on a different server/project than the one this app is currently using.\n\n' +
              `Your account server: ${payload.iss}\n` +
              `Current app server: bjhtlokaafsnoscgecct.supabase.co\n\n` +
              'This happens when:\n' +
              '• You signed up on a development/test version\n' +
              '• The app was moved to a different server\n' +
              '• You have accounts on multiple versions\n\n' +
              'Would you like to sign out so you can create a new account on the correct server?\n\n' +
              'Click OK to sign out, or Cancel to keep the current account.'
            );
            
            if (shouldSignOut) {
              console.log('🔄 User chose to sign out due to project mismatch');
              // Use the signOut from useAuth hook
              if (typeof window !== 'undefined' && window.location) {
                // Sign out and reload the page to clear all auth state
                localStorage.clear();
                sessionStorage.clear();
                window.location.reload();
              }
              // eslint-disable-next-line no-restricted-globals
              alert('✅ You have been signed out.\n\nPlease create a new account or sign in with an account that was created on this server.');
              return;
            }
          }
        }
      } catch (decodeError) {
        console.error('❌ Failed to decode JWT token:', decodeError);
      }
    }
    
    // Check if there's a mismatch
    const storedUrl = localStorage.getItem('supabase_url');
    const expectedUrl = 'https://bjhtlokaafsnoscgecct.supabase.co';
    if (storedUrl && storedUrl !== expectedUrl) {
      console.error('❌ URL MISMATCH: localStorage has different URL than expected!');
      console.error('Stored:', storedUrl);
      console.error('Expected:', expectedUrl);
    }
    
    // Check if the anon key looks valid (JWT format) - already analyzed above
    
    // 3. Test basic database connectivity with multiple approaches
    let databaseWorking = false;
    let authWorking = false;
    
    // First, test if auth endpoint is working (we know this works from earlier tests)
    try {
      console.log('🔐 Verifying auth endpoint accessibility...');
      const authResponse = await fetch(`${supabase.supabaseUrl}/auth/v1/settings`, {
        method: 'GET',
        headers: {
          'apikey': supabase.supabaseKey
        }
      });
      authWorking = authResponse.status === 200;
      console.log(authWorking ? '✅ Auth endpoint confirmed working' : '❌ Auth endpoint not working');
    } catch (authError) {
      console.error('❌ Auth endpoint test failed:', authError);
    }
    
    // Now test database connectivity
    try {
      console.log('🗄️ Testing database connectivity...');
      
      // Try a simple table that should exist
      const { error } = await supabase
        .from('app_settings')
        .select('key')
        .limit(1);
        
      if (error) {
        console.error('❌ Database connectivity failed:', error);
        
        // Check if it's a table not found vs connectivity issue
        if (error.message.includes('relation "app_settings" does not exist')) {
          console.log('ℹ️ Table not found, but database is accessible');
          databaseWorking = true;
        } else {
          databaseWorking = false;
        }
      } else {
        console.log('✅ Database connectivity working');
        databaseWorking = true;
      }
    } catch (error) {
      console.error('❌ Database connectivity test failed:', error);
      databaseWorking = false;
    }
    
    // Determine the result based on what's working
    if (databaseWorking) {
      return true; // Full connectivity
    } else if (authWorking) {
      console.log('🔄 Database blocked, but auth works - enabling auth-only mode...');
      return 'auth_only'; // Auth-only mode
    } else {
      console.error('❌ Both database and auth endpoints failing');
      return false; // Complete failure
    }
  };

  // Test general network connectivity to Supabase
  // eslint-disable-next-line no-unused-vars -- dead code, kept for now
  const testNetworkConnectivity = async () => {
    return await runSupabaseDiagnostics();
  };

  // Check for and sync pending profile updates
  const checkAndSyncPendingUpdates = useCallback(async () => {
    const pendingUpdate = localStorage.getItem('pending_profile_update');
    setHasPendingSync(!!pendingUpdate);
    
    if (pendingUpdate && isAuthenticated && user) {
      try {
        const updateData = JSON.parse(pendingUpdate);
        console.log('🔄 Found pending profile update, attempting to sync...');
        
        const { error } = await supabase.auth.updateUser({
          data: {
            full_name: updateData.name,
            profile_picture: updateData.profilePicture
          }
        });

        if (!error) {
          console.log('✅ Pending profile update synced successfully');
          localStorage.removeItem('pending_profile_update');
          setHasPendingSync(false);
          
          // Update current display with synced data
          const userData = {
            name: updateData.name,
            profilePicture: updateData.profilePicture
          };
          setProfileData(userData);
          setTempProfileData(userData);
        } else {
          console.log('⏳ Still unable to sync pending update, will try again later');
        }
      } catch (error) {
        console.log('⏳ Connection still unavailable, will retry sync later');
      }
    }
  }, [isAuthenticated, user]);

  // Load current user profile data when modal opens
  useEffect(() => {
    const loadProfileData = async () => {
      if (isVisible) {
        const currentUser = getCurrentUser();
        if (currentUser) {
          let userData = {
            name: currentUser.name || '',
            profilePicture: currentUser.profilePicture || null
          };
          
          // For authenticated users, load fresh data from user_profiles table
          if (currentUser.needsProfileRefresh && currentUser.userId) {
            console.log('🔄 Loading fresh profile data from user_profiles table...');
            try {
              const { data: profileData, error } = await supabase
                .from('user_profiles')
                .select('full_name, avatar_url')
                .eq('user_id', currentUser.userId)
                .single();
              
              if (!error && profileData) {
                console.log('✅ Loaded fresh profile data:', profileData);
                userData = {
                  name: profileData.full_name || currentUser.email,
                  profilePicture: profileData.avatar_url || null
                };
              } else {
                console.log('ℹ️ No profile data found in user_profiles table, using auth metadata');
              }
            } catch (error) {
              console.warn('⚠️ Failed to load profile data from user_profiles table:', error);
              // Fall back to auth metadata
            }
          }
          
          setProfileData(userData);
          setTempProfileData(userData);
          setIsEditing(false); // Reset editing state when modal opens
        }
        
        // Check for pending profile updates to sync
        checkAndSyncPendingUpdates();
      }
    };
    
    loadProfileData();
  }, [isVisible, getCurrentUser, checkAndSyncPendingUpdates]);

  // Handle profile picture upload
  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Profile picture must be smaller than 2MB.');
      return;
    }

    setUploading(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        setTempProfileData(prev => ({
          ...prev,
          profilePicture: e.target.result
        }));
        setUploading(false);
      };
      reader.onerror = () => {
        console.error('FileReader error');
        alert('Failed to read file. Please try again.');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      alert('Failed to upload profile picture. Please try again.');
      setUploading(false);
    }

    // Reset file input
    event.target.value = '';
  };

  // Remove profile picture
  const handleRemoveProfilePicture = () => {
    setTempProfileData(prev => ({
      ...prev,
      profilePicture: null
    }));
  };

  // Save profile changes
  const handleSaveProfile = async () => {
    console.log('🔄 handleSaveProfile called with:', {
      name: tempProfileData.name,
      profilePicture: tempProfileData.profilePicture ? 'Present' : 'None',
      isAuthenticated,
      currentUser: getCurrentUser()
    });

    if (!tempProfileData.name.trim()) {
      alert('Name cannot be empty.');
      return;
    }

    setSaving(true);
    try {
      const currentUser = getCurrentUser();
      console.log('🔍 Current user:', currentUser);
      console.log('🔍 isAuthenticated:', isAuthenticated);
      console.log('🔍 user:', user);
      
      if (isAuthenticated && user) {
        // For authenticated users, update Supabase profile
        console.log('📝 Updating authenticated user profile via user_profiles table...');
        console.log('📝 Profile data to update:', {
          full_name: tempProfileData.name.trim(),
          avatar_url: tempProfileData.profilePicture
        });
        console.log('📝 Current user object:', user);
        
        // Skip the problematic supabase.auth.updateUser() and go directly to user_profiles table
        console.log('🔄 Using user_profiles table directly (auth.updateUser has known issues)...');
        
        try {
          const { error: profileError } = await supabase
            .from('user_profiles')
            .upsert([{
              user_id: user.id,
              email: user.email,
              full_name: tempProfileData.name.trim(),
              avatar_url: tempProfileData.profilePicture,
              updated_at: new Date().toISOString()
            }], {
              onConflict: 'user_id'
            });

          if (profileError) {
            throw profileError;
          }

          console.log('✅ Profile updated successfully via user_profiles table');
          
          // Refresh profile data from the database to ensure we have the latest
          console.log('🔄 Refreshing profile data after successful update...');
          try {
            const { data: refreshedProfile, error: refreshError } = await supabase
              .from('user_profiles')
              .select('full_name, avatar_url')
              .eq('user_id', user.id)
              .single();
            
            if (!refreshError && refreshedProfile) {
              const refreshedData = {
                name: refreshedProfile.full_name || user.email,
                profilePicture: refreshedProfile.avatar_url || null
              };
              console.log('✅ Refreshed profile data:', refreshedData);
              setProfileData(refreshedData);
              setTempProfileData(refreshedData);
            } else {
              // Fallback to the data we just saved
              setProfileData(tempProfileData);
              setTempProfileData(tempProfileData);
            }
          } catch (refreshError) {
            console.warn('⚠️ Failed to refresh profile data, using saved data:', refreshError);
            setProfileData(tempProfileData);
            setTempProfileData(tempProfileData);
          }
          
          alert('✅ Profile updated successfully!');
          setIsEditing(false);
          
          // Trigger header refresh
          triggerProfileRefresh();
          
          return; // Exit early, success
          
        } catch (directUpdateError) {
          console.error('❌ Direct user_profiles update failed:', directUpdateError);
          throw directUpdateError;
        }
        
        /* OLD COMPLEX LOGIC - COMMENTED OUT SINCE DIRECT user_profiles APPROACH WORKS
        // Test network connectivity first
        const networkResult = await testNetworkConnectivity();
        if (networkResult === false) {
          console.error('❌ General network connectivity to Supabase is failing');
          throw new Error('Unable to connect to Supabase services. Please check your internet connection and try again.');
        }
        
        const authOnlyMode = networkResult === 'auth_only';
        if (authOnlyMode) {
          console.log('🔄 Running in AUTH-ONLY mode due to database endpoint blocking');
          alert('ℹ️ Network Note: Database endpoints appear to be blocked by your network, but authentication works. Profile updates will use auth-only mode.');
        }

        // Test auth endpoint connectivity
        const authEndpointWorking = await testAuthEndpoint();
        if (!authEndpointWorking) {
          console.error('❌ Supabase Auth service is currently unavailable');
          throw new Error('Supabase Auth service is temporarily unavailable. This is different from the database connection and may be a service-specific issue. Please try again later or contact support if the issue persists.');
        }
        
        // RLS DEBUG: Test current session validity
        console.log('🔍 RLS DEBUG: Testing current session...');
        try {
          const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
          if (sessionError) {
            console.error('🚨 RLS DEBUG: Session error:', sessionError);
          } else {
            console.log('✅ RLS DEBUG: Session is valid');
            console.log('Session user:', sessionData.session?.user?.id);
            console.log('Session expires:', sessionData.session?.expires_at);
          }
        } catch (sessionTestError) {
          console.error('🚨 RLS DEBUG: Session test failed:', sessionTestError);
        }
        
        // Try a minimal update first (name only) to test if it's a payload size issue
        console.log('🧪 Testing with name-only update first...');
        console.log('🔍 RLS DEBUG: Current user session details:');
        console.log('User ID:', user.id);
        console.log('User email:', user.email);
        console.log('User role:', user.role);
        console.log('Auth role from JWT:', user.aud);
        
        // CRITICAL DEBUG: Let's check what's happening with the actual request
        console.log('🔍 CRITICAL: About to make PUT request to:', `https://bjhtlokaafsnoscgecct.supabase.co/auth/v1/user`);
        console.log('🔍 CRITICAL: Using API key (first 20):', localStorage.getItem('supabase_anon_key')?.substring(0, 20));
        console.log('🔍 CRITICAL: Supabase client exists:', !!supabase);
        console.log('🔍 CRITICAL: Supabase URL:', supabase?.supabaseUrl);
        console.log('🔍 CRITICAL: Supabase Key (first 20):', supabase?.supabaseKey?.substring(0, 20));
        
        // CRITICAL: Let's see if this is specifically a Supabase project issue
        console.log('🚨 CRITICAL HYPOTHESIS TEST:');
        console.log('If this is a project configuration issue, we should see:');
        console.log('- GET requests work (status 200/404)'); 
        console.log('- PUT/POST requests fail (ERR_CONNECTION_CLOSED/RESET)');
        console.log('This suggests: Project write permissions disabled, billing issue, or project suspended');
        
        // CRITICAL: Since upgrading to paid didn't fix it, let's test a different theory
        console.log('🚨 NEW THEORY: Since paid upgrade did not fix the issue...');
        console.log('This could be:');
        console.log('1. Supabase project region/infrastructure issue');
        console.log('2. Project-specific service degradation');
        console.log('3. Authentication service misconfiguration');
        console.log('4. Load balancer routing problem');
        
        // Test a simple GET request to the same auth endpoint that works
        try {
          const testAuthGet = await fetch('https://bjhtlokaafsnoscgecct.supabase.co/auth/v1/settings', {
            method: 'GET',
            headers: {
              'apikey': localStorage.getItem('supabase_anon_key') || '',
              'Authorization': `Bearer ${localStorage.getItem('supabase_anon_key') || ''}`
            }
          });
          console.log('🔍 CRITICAL: GET auth/v1/settings status:', testAuthGet.status);
          
          // Now test PUT to same endpoint
          const testAuthPut = await fetch('https://bjhtlokaafsnoscgecct.supabase.co/auth/v1/settings', {
            method: 'PUT',
            headers: {
              'apikey': localStorage.getItem('supabase_anon_key') || '',
              'Authorization': `Bearer ${localStorage.getItem('supabase_anon_key') || ''}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({})
          });
          console.log('🔍 CRITICAL: PUT auth/v1/settings status:', testAuthPut.status);
        } catch (methodTestError) {
          console.error('🚨 CRITICAL: HTTP method test failed:', methodTestError);
          
          // DEFINITIVE TEST: Try the exact same request to a different Supabase project
          console.log('🧪 DEFINITIVE TEST: Testing if this is project-specific...');
          console.log('If this is a project-specific issue, the same request pattern should work on other Supabase projects');
          console.log('ERROR PATTERN ANALYSIS:');
          console.log('- ERR_CONNECTION_CLOSED = Server actively closing connections');
          console.log('- This happens when server rejects the connection type/method');
          console.log('- Likely causes: Project-level restrictions, auth service issues, or infrastructure problems');
        }
        
        try {
          const { error: nameOnlyError } = await supabase.auth.updateUser({
            data: {
              full_name: tempProfileData.name.trim()
            }
          });
          
          if (nameOnlyError) {
            console.error('🚨 RLS DEBUG: Auth update error details:', {
              message: nameOnlyError.message,
              code: nameOnlyError.code,
              status: nameOnlyError.status,
              details: nameOnlyError.details
            });
          }
          
          if (!nameOnlyError) {
            console.log('✅ Name-only update succeeded! Issue might be with profile picture.');
            
            // If name update worked, try adding profile picture separately
            if (tempProfileData.profilePicture) {
              console.log('🖼️ Attempting to add profile picture...');
              const { error: pictureError } = await supabase.auth.updateUser({
                data: {
                  full_name: tempProfileData.name.trim(),
                  profile_picture: tempProfileData.profilePicture
                }
              });
              
              if (pictureError) {
                console.warn('⚠️ Profile picture update failed, but name was saved');
                alert('✅ Your name was updated successfully!\n\n⚠️ However, the profile picture could not be saved. This might be due to size or format restrictions.');
                
                setProfileData({
                  name: tempProfileData.name,
                  profilePicture: profileData.profilePicture // Keep old picture
                });
                setTempProfileData({
                  name: tempProfileData.name,
                  profilePicture: profileData.profilePicture
                });
                setIsEditing(false);
                return;
              } else {
                console.log('✅ Both name and profile picture updated successfully');
              }
            }
            
            // Full success
            setProfileData(tempProfileData);
            setIsEditing(false);
            alert('Profile updated successfully!');
            return;
          } else {
            console.error('❌ Even name-only update failed:', nameOnlyError);
            throw nameOnlyError;
          }
        } catch (testError) {
          console.error('❌ Name-only test update failed:', testError);
          // Continue to original logic with full payload
        }
        
        try {
          console.log('✅ Auth endpoint is accessible, proceeding with update...');
          
          // Check data size and format
          const updateData = {
            full_name: tempProfileData.name.trim(),
            profile_picture: tempProfileData.profilePicture
          };
          
          // Log data size for debugging
          const dataSize = JSON.stringify(updateData).length;
          console.log(`📊 Update data size: ${dataSize} bytes`);
          
          if (updateData.profile_picture) {
            console.log(`🖼️ Profile picture type: ${typeof updateData.profile_picture}`);
            console.log(`🖼️ Profile picture length: ${updateData.profile_picture.length} characters`);
            
            // Check if it's a valid base64 data URL
            if (typeof updateData.profile_picture === 'string' && updateData.profile_picture.startsWith('data:')) {
              const base64Data = updateData.profile_picture.split(',')[1];
              const sizeInBytes = Math.ceil(base64Data.length * 0.75);
              console.log(`🖼️ Profile picture size: ${Math.round(sizeInBytes / 1024)} KB`);
              
              // Warn if image is very large
              if (sizeInBytes > 1024 * 1024) { // 1MB
                console.warn('⚠️ Profile picture is very large (>1MB), this might cause issues');
              }
            }
          }
          
          const { error } = await supabase.auth.updateUser({
            data: updateData
          });

          if (error) {
            console.error('❌ Supabase auth update error:', error);
            
            // If there's a profile picture, try updating without it (might be too large)
            if (updateData.profile_picture) {
              console.log('🔄 Retrying update without profile picture...');
              const { error: retryError } = await supabase.auth.updateUser({
                data: {
                  full_name: updateData.full_name
                }
              });
              
              if (retryError) {
                console.error('❌ Retry without profile picture also failed:', retryError);
                throw retryError;
              } else {
                console.log('✅ Name updated successfully (without profile picture)');
                alert('⚠️ Your name was updated, but the profile picture could not be saved. The image might be too large or in an unsupported format.');
                
                // Update local state to reflect partial success
                setProfileData({
                  name: tempProfileData.name,
                  profilePicture: profileData.profilePicture // Keep old picture
                });
                setTempProfileData({
                  name: tempProfileData.name,
                  profilePicture: profileData.profilePicture
                });
                setIsEditing(false);
                return; // Exit early
              }
            } else {
              throw error;
            }
          }
          console.log('✅ Authenticated user profile updated successfully');
        } catch (supabaseError) {
          console.error('❌ Supabase Auth endpoint error:', supabaseError);
          
          // Check if it's the specific auth endpoint issue
          if (supabaseError.message?.includes('Failed to fetch') || 
              supabaseError.message?.includes('ERR_CONNECTION_CLOSED') ||
              supabaseError.name === 'AuthRetryableFetchError') {
            
            console.log('🔄 Auth endpoint unavailable, trying alternative storage method...');
            
            // Alternative: Store in custom user_profiles table instead of auth metadata
            try {
              const { error: profileError } = await supabase
                .from('user_profiles')
                .upsert([{
                  user_id: user.id,
                  email: user.email,
                  full_name: tempProfileData.name.trim(),
                  avatar_url: tempProfileData.profilePicture,
                  updated_at: new Date().toISOString()
                }], {
                  onConflict: 'user_id'
                });

              if (profileError) {
                throw profileError;
              }

              console.log('✅ Profile updated via alternative method (user_profiles table)');
              alert('✅ Profile updated successfully!\n\nNote: Due to a temporary issue with the auth service, your profile was saved using an alternative method.');
              
              // Update local state
              setProfileData(tempProfileData);
              setIsEditing(false);
              return; // Exit early, success
              
            } catch (alternativeError) {
              console.error('❌ Alternative storage method also failed:', alternativeError);
              
              // Final fallback: local storage with sync later
              console.log('🔄 Falling back to local storage...');
              
              const fallbackUser = {
                email: user.email,
                name: tempProfileData.name.trim(),
                profilePicture: tempProfileData.profilePicture,
                isAuthenticated: true,
                lastUpdated: new Date().toISOString(),
                pendingSync: true
              };
              
              localStorage.setItem('pending_profile_update', JSON.stringify(fallbackUser));
              setHasPendingSync(true);
              
              console.log('💾 Profile update saved locally, will sync when auth service is restored');
              alert('⚠️ Supabase Auth service is temporarily unavailable.\n\nYour profile changes have been saved locally and will be synced when the service is restored.\n\nThis appears to be a service-specific issue, not a general connection problem.');
              
              // Update local state so UI reflects the changes
              setProfileData(tempProfileData);
              setIsEditing(false);
              return; // Exit early, don't throw error
            }
          } else {
            // Re-throw non-connection errors
            throw new Error(`Authentication update failed: ${supabaseError.message}`);
          }
        }
        END OF OLD COMPLEX LOGIC */
        
      } else {
        console.error('❌ No valid user found for profile update');
        throw new Error('No valid user session found. Please try signing in again.');
      }
      // NOTE: no tail here on purpose. The signed-in path already calls
      // setProfileData / setIsEditing / alert before returning, and the else
      // branch throws - so anything after this point is unreachable. It used to
      // be the fall-through for the removed email-only path.
    } catch (error) {
      console.error('❌ Error saving profile:', error);
      const errorMessage = error.message || 'Unknown error occurred';
      alert(`Failed to save profile: ${errorMessage}\n\nPlease check the console for more details.`);
    } finally {
      setSaving(false);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setTempProfileData(profileData);
    setIsEditing(false);
  };

  const currentUser = getCurrentUser();
  if (!isVisible || !currentUser) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl max-w-md w-full">
        <div className="bg-gradient-to-r from-purple-900 to-orange-900 px-6 py-4 border-b-2 border-orange-500">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-orange-100 flex items-center gap-2">
              <User size={20} />
              User Profile
              {hasPendingSync && (
                <span className="bg-yellow-600 text-yellow-100 px-2 py-1 rounded-full text-xs font-semibold animate-pulse">
                  Sync Pending
                </span>
              )}
            </h2>
            <button
              onClick={onClose}
              className="bg-red-600 hover:bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {/* Profile Picture Section */}
          <div className="text-center mb-6">
            <div className="relative inline-block">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden bg-gray-700 border-4 border-orange-500">
                {(isEditing ? tempProfileData.profilePicture : profileData.profilePicture) ? (
                  <img
                    src={isEditing ? tempProfileData.profilePicture : profileData.profilePicture}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <User size={32} />
                  </div>
                )}
              </div>
              
              {isEditing && (
                <div className="flex justify-center gap-2 mb-4">
                  <label
                    htmlFor="profile-picture-upload"
                    className={`cursor-pointer bg-blue-600 hover:bg-blue-500 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
                      uploading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Camera size={16} />
                    {uploading ? 'Uploading...' : 'Upload'}
                  </label>
                  <input
                    id="profile-picture-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureUpload}
                    disabled={uploading}
                    className="hidden"
                  />
                  
                  {tempProfileData.profilePicture && (
                    <button
                      onClick={handleRemoveProfilePicture}
                      className="bg-red-600 hover:bg-red-500 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Name Section */}
          <div className="mb-6">
            <label className="block text-orange-300 text-sm font-bold mb-2">Name</label>
            {isEditing ? (
              <input
                type="text"
                value={tempProfileData.name}
                onChange={(e) => {
                  setTempProfileData(prev => ({
                    ...prev,
                    name: e.target.value
                  }));
                }}
                className="w-full bg-gray-700 border-2 border-orange-600 text-orange-100 rounded-md px-3 py-2"
                placeholder="Enter your name"
              />
            ) : (
              <div className="w-full bg-gray-700 border-2 border-gray-600 text-orange-100 rounded-md px-3 py-2">
                {profileData.name || 'No name set'}
              </div>
            )}
          </div>

          {/* User Type Info */}
          <div className="mb-6">
            <label className="block text-orange-300 text-sm font-bold mb-2">Account Type</label>
            <div className="w-full bg-gray-700 border-2 border-gray-600 text-orange-100 rounded-md px-3 py-2">
              <span className="flex items-center gap-2">
                🔐 Signed in
                <span className="text-xs text-gray-400">({currentUser.email})</span>
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={saving || uploading}
                  className={`bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 ${
                    (saving || uploading) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <Save size={16} />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2"
              >
                <Edit3 size={16} />
                Edit Profile
              </button>
            )}
          </div>

          {/* Upload Guidelines */}
          {isEditing && (
            <div className="mt-4 p-3 bg-blue-900 bg-opacity-30 border border-blue-600 rounded-lg">
              <p className="text-blue-200 text-xs">
                <strong>Profile Picture Guidelines:</strong><br />
                • Supported formats: JPG, PNG, GIF, WebP<br />
                • Maximum size: 2MB<br />
                • Recommended: Square images work best
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
